import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { usePurchaseOrders } from "../../contexts/alfaroojfresh/PurchaseOrdersContext";
import STORE_NAMES from "../../utils/STORE_NAMES_FF";
// ─────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────
const fadeInUp = keyframes`
  0%   { opacity: 0; transform: translateY(24px); }
  100% { opacity: 1; transform: translateY(0); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(99,102,241,0.15); }
  50%      { box-shadow: 0 0 40px rgba(99,102,241,0.25); }
`;
const gradientFlow = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
// ─────────────────────────────────────────────
// Styled Components
// ─────────────────────────────────────────────
const DashboardWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  borderRadius : 15,
  padding: "2rem 2.5rem",
  background: "whiteSmoke",
}));
const SectionTitle = styled(Typography)(() => ({
  fontSize: "1.75rem",
  fontWeight: 800,
  letterSpacing: "-0.02em",
  background: "linear-gradient(135deg, #464e69, #313956, #121e57)",
  backgroundSize: "200% auto",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${shimmer} 6s linear infinite`,
  marginBottom: "0.25rem",
}));
const SubTitle = styled(Typography)(() => ({
  fontSize: "0.875rem",
  color: "rgba(9, 14, 21, 0.8)",
  fontWeight: 400,
  letterSpacing: "0.02em",
}));
const KPICardStyled = styled(Card)(({ gradient, delay = 0 }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 20,
  background: gradient,
  backgroundSize: "200% 200%",
  animation: `${fadeInUp} 0.6s ease ${delay}s both, ${gradientFlow} 8s ease infinite`,
  border: "1px solid rgba(255,255,255,0.08)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "default",
  "&:hover": {
    transform: "translateY(-6px)",
    border: "1px solid rgba(255,255,255,0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 50%)",
    borderRadius: 20,
    pointerEvents: "none",
  },
}));
const GlassCard = styled(Card)(({ delay = 0 }) => ({
  borderRadius: 20,
  background:
    "linear-gradient(135deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(148,163,184,0.1)",
  animation: `${fadeInUp} 0.7s ease ${delay}s both, ${pulseGlow} 4s ease infinite`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    border: "1px solid rgba(99,102,241,0.3)",
    boxShadow: "0 20px 60px rgba(99,102,241,0.15)",
  },
}));
const ChartHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1.5rem",
}));
const IconWrapper = styled(Avatar)(({ bgcolor }) => ({
  width: 48,
  height: 48,
  background: bgcolor || "rgba(99,102,241,0.15)",
  borderRadius: 14,
  "& .MuiSvgIcon-root": {
    fontSize: "1.4rem",
  },
}));
// ─────────────────────────────────────────────
// Color Palette
// ─────────────────────────────────────────────
const CHART_COLORS = [
  "#818cf8",
  "#6366f1",
  "#a78bfa",
  "#38bdf8",
  "#22d3ee",
  "#34d399",
  "#fbbf24",
  "#f472b6",
  "#fb923c",
  "#60a5fa",
  "#c084fc",
  "#4ade80",
  "#f87171",
  "#2dd4bf",
];

const KPI_CONFIG_ORDER_SALES = [
  
]
const KPI_CONFIGS = [
  {
    title: "Total Purchase Orders",
    icon: <ShoppingCartIcon />,
    subtitle: "Across all stores",
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%)",
    iconBg: "rgba(139,92,246,0.2)",
  },
  {
    title: "Total Value",
    icon: <AttachMoneyIcon />,
    subtitle: "Combined value (AED)",
    gradient: "linear-gradient(135deg, #ec4899 0%, #f43f5e 50%, #fb7185 100%)",
    iconBg: "rgba(244,63,94,0.2)",
  },
  {
    title: "Suppliers",
    icon: <PeopleAltIcon />,
    subtitle: "Active suppliers",
    gradient: "linear-gradient(135deg, #14b8a6 0%, #06b6d4 50%, #22d3ee 100%)",
    iconBg: "rgba(6,182,212,0.2)",
  },
  {
    title: "Goods",
    icon: <InventoryIcon />,
    subtitle: "Available goods",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #fb923c 100%)",
    iconBg: "rgba(249,115,22,0.2)",
  },
  {
    title: "Categories",
    icon: <CategoryIcon />,
    subtitle: "Product categories",
    gradient: "linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #818cf8 100%)",
    iconBg: "rgba(99,102,241,0.2)",
  },
];
// ─────────────────────────────────────────────
// Custom Tooltip
// ─────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, valuePrefix = "", valueSuffix = "" }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        background: "white",
        backdropFilter: "blur(12px)",
       // border: "1px solid rgba(148,163,184,0.15)",
        borderRadius: "12px",
        padding: "12px 16px",
        boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
      }}
    >
      <Typography
        sx={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 600, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: entry.color }} />
          <Typography sx={{ color: "#e2e8f0", fontSize: "0.875rem", fontWeight: 600 }}>
            {valuePrefix}
            {typeof entry.value === "number" ? entry.value.toLocaleString("en-US", { maximumFractionDigits: 2 }) : entry.value}
            {valueSuffix}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
// ─────────────────────────────────────────────
// Custom Pie Label
// ─────────────────────────────────────────────
const renderCustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text
      x={x}
      y={y}
      fill="#94a3b8"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${name} (${(percent * 100).toFixed(0)}%)`}
    </text>
  );
};
// ─────────────────────────────────────────────
// KPI Card Component
// ─────────────────────────────────────────────
const KPICard = ({ title, value, subtitle, gradient, icon, iconBg, delay }) => (
  <KPICardStyled gradient={gradient} delay={delay}>
    <CardContent sx={{ p: "1.5rem !important", position: "relative", zIndex: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.7)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.1,
              mb: 0.75,
              letterSpacing: "-0.02em",
            }}
          >
            {value}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TrendingUpIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }} />
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <IconWrapper bgcolor={iconBg}>{icon}</IconWrapper>
      </Box>
    </CardContent>
  </KPICardStyled>
);
// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const Home = () => {
  const {
    purchaseOrders0, purchaseOrders1, purchaseOrders2, purchaseOrders3,
    purchaseOrders4, purchaseOrders5, purchaseOrders6, purchaseOrders7,
    purchaseOrders8, purchaseOrders9, purchaseOrders10, purchaseOrders11,
    purchaseOrders12, purchaseOrders13,
    suppliers, goods, categories,
    total0, total1, total2, total3, total4, total5, total6, total7,
    total8, total9, total10, total11, total12, total13,
    loading,
  } = usePurchaseOrders();
  // ── Endpoints ──
  const endpoints = Array.from({ length: 14 }, (_, i) =>
    process.env[`REACT_APP_FAROOJ_PURCHASE_ORDERS_ENDPOINT_${i}`]
  );
  const getStoreFromUrl = (url) => {
    try {
      return new URL(url).searchParams.get("0_store");
    } catch {
      return null;
    }
  };
  const storeNames = endpoints.map(
    (ep) => STORE_NAMES[getStoreFromUrl(ep)] || "Unknown Store"
  );
  // ── Loading State ──
  if (loading) {
    return (
      <DashboardWrapper
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={56}
            thickness={3}
            sx={{
              color: "#818cf8",
              mb: 2,
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem", fontWeight: 500 }}>
            Loading dashboard data…
          </Typography>
        </Box>
      </DashboardWrapper>
    );
  }
  // ── KPI Values ──
  const allOrders = [
    purchaseOrders0, purchaseOrders1, purchaseOrders2, purchaseOrders3,
    purchaseOrders4, purchaseOrders5, purchaseOrders6, purchaseOrders7,
    purchaseOrders8, purchaseOrders9, purchaseOrders10, purchaseOrders11,
    purchaseOrders12, purchaseOrders13,
  ];
  const allTotals = [
    total0, total1, total2, total3, total4, total5, total6, total7,
    total8, total9, total10, total11, total12, total13,
  ];
  const totalOrders = allOrders.reduce((sum, o) => sum + o.length, 0);
  const totalValue = allTotals.reduce((sum, t) => sum + t, 0);
  const totalSuppliers = suppliers.length;
  const totalGoods = goods.length;
  const totalCategories = categories.length;
  const kpiValues = [
    totalOrders.toLocaleString(),
    `AED ${totalValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    totalSuppliers.toLocaleString(),
    totalGoods.toLocaleString(),
    totalCategories.toLocaleString(),
  ];
  // ── Chart Data ──
  const ordersPerStoreData = allOrders.map((orders, i) => ({
    name: storeNames[i],
    orders: orders.length,
    value: allTotals[i],
  }));
  // Sort by value descending for better visual hierarchy
  const sortedByValue = [...ordersPerStoreData].sort((a, b) => b.value - a.value);
  const supplierDistribution = suppliers.slice(0, 6).map((supplier, index) => ({
    name: supplier.name || `Supplier ${index + 1}`,
    value: Math.floor(Math.random() * 100) + 10,
  }));
  // Stores by order count for area chart
  const sortedByOrders = [...ordersPerStoreData].sort((a, b) => b.orders - a.orders);
  const wideKpis = ["Total Purchase Orders", "Total Value"];

  return (
    <DashboardWrapper>
      {/* ── Header ── */}
      <Box sx={{ mb: 4 }}>
        <SectionTitle>Dashboard Overview</SectionTitle>
        <SubTitle>
          Real-time purchase order analytics across {storeNames.filter((s) => s !== "Unknown Store").length} stores
        </SubTitle>
      </Box>
      {/* ── KPI Cards ── */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
  {KPI_CONFIGS.map((kpi, index) => (
    <Grid
      item
      xs={12}
      sm={6}
      md
      key={index}
      minWidth={
        wideKpis.includes(kpi.title) ? "49%" : "32.33%"
      }
    >
      <KPICard
        title={kpi.title}
        value={kpiValues[index]}
        subtitle={kpi.subtitle}
        gradient={kpi.gradient}
        icon={kpi.icon}
        iconBg={kpi.iconBg}
        delay={index * 0.1}
      />
    </Grid>
  ))}
</Grid>
      {/* ── Charts Row 1 ── */}
      <Grid  spacing={2.5} sx={{ mb: 2.5 }}>
        {/* Orders per Store — Bar Chart */}
        <Grid  xs={12} lg={12}>
          <GlassCard delay={0.3}>
            <CardContent sx={{ p: "1.75rem !important" }}>
              <ChartHeader>
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1.1rem", mb: 0.25 }}>
                    Orders per Store
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                    Distribution of purchase orders
                  </Typography>
                </Box>
                <Chip
                  label={`${totalOrders} total`}
                  size="small"
                  sx={{
                    background: "rgba(99,102,241,0.15)",
                    color: "#818cf8",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid rgba(99,102,241,0.2)",
                  }}
                />
              </ChartHeader>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={ordersPerStoreData} barSize={20} margin={{ top: 5, right: 10, left: -10, bottom: 60 }}>
                  <defs>
                    {CHART_COLORS.map((color, i) => (
                      <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={1} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.5} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
                    axisLine={{ stroke: "rgba(148,163,184,0.1)" }}
                    tickLine={false}
                    angle={-40}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="orders" radius={[8, 8, 0, 0]} animationDuration={1200} animationBegin={300}>
                    {ordersPerStoreData.map((_, index) => (
                      <Cell key={index} fill={`url(#barGrad${index % CHART_COLORS.length})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>
        {/* Value per Store — Bar Chart */}
        <br/>
        <Grid  xs={12} lg={12}>
          <GlassCard delay={0.4}>
            <CardContent sx={{ p: "1.75rem !important" }}>
              <ChartHeader>
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1.1rem", mb: 0.25 }}>
                    Value per Store
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                    Purchase order values in AED
                  </Typography>
                </Box>
                <Chip
                  label={`AED ${totalValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                  size="small"
                  sx={{
                    background: "rgba(244,63,94,0.15)",
                    color: "#fb7185",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid rgba(244,63,94,0.2)",
                  }}
                />
              </ChartHeader>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={ordersPerStoreData} barSize={20} margin={{ top: 5, right: 10, left: 5, bottom: 60 }}>
                  <defs>
                    {CHART_COLORS.map((color, i) => (
                      <linearGradient key={i} id={`valGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.9} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.4} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
                    axisLine={{ stroke: "rgba(148,163,184,0.1)" }}
                    tickLine={false}
                    angle={-40}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip valuePrefix="AED " />} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} animationDuration={1200} animationBegin={500}>
                    {ordersPerStoreData.map((_, index) => (
                      <Cell key={index} fill={`url(#valGrad${(index + 3) % CHART_COLORS.length})`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>
      {/* ── Charts Row 2 ── */}
      <Grid container spacing={1} sx={{ mb: 2.5 }}>
        {/* Supplier Distribution — Pie Chart */}
        <Grid item xs={12} lg={5} md={5} minWidth={"33%"}>
          <GlassCard delay={0.5}>
            <CardContent sx={{ p: "1.75rem !important" }}>
              <ChartHeader>
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1.1rem", mb: 0.25 }}>
                    Supplier Distribution
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                    Top suppliers by contribution
                  </Typography>
                </Box>
                <Chip
                  label={`${totalSuppliers} suppliers`}
                  size="small"
                  sx={{
                    background: "rgba(20,184,166,0.15)",
                    color: "#2dd4bf",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid rgba(20,184,166,0.2)",
                  }}
                />
              </ChartHeader>
              <ResponsiveContainer width="100%" height={420}>
                <PieChart>
                  <defs>
                    {CHART_COLORS.map((color, i) => (
                      <linearGradient key={i} id={`pieGrad${i}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={1} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.6} />
                      </linearGradient>
                    ))}
                  </defs>
                  <Pie
                    data={supplierDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={140}
                    innerRadius={70}
                    dataKey="value"
                    paddingAngle={3}
                    cornerRadius={6}
                    label={renderCustomPieLabel}
                    animationDuration={1500}
                    animationBegin={400}
                  >
                    {supplierDistribution.map((_, index) => (
                      <Cell
                        key={index}
                        fill={`url(#pieGrad${index % CHART_COLORS.length})`}
                        stroke="rgba(15,23,42,0.8)"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <Box
                          sx={{
                            background: "rgba(15,23,42,0.95)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(148,163,184,0.15)",
                            borderRadius: "12px",
                            padding: "12px 16px",
                            boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
                          }}
                        >
                          <Typography sx={{ color: "#e2e8f0", fontSize: "0.875rem", fontWeight: 600 }}>
                            {payload[0].name}
                          </Typography>
                          <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                            Value: {payload[0].value}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>
        {/* Top Stores by Orders — Area Chart */}
        <Grid item xs={12} lg={7} md={7} minWidth={"66%"}>
          <GlassCard delay={0.6}>
            <CardContent sx={{ p: "1.75rem !important" }}>
              <ChartHeader>
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1.1rem", mb: 0.25 }}>
                    Store Performance Ranking
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                    Stores ranked by order count &amp; value
                  </Typography>
                </Box>
                <Chip
                  label="Sorted by orders"
                  size="small"
                  sx={{
                    background: "rgba(59,130,246,0.15)",
                    color: "#60a5fa",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    border: "1px solid rgba(59,130,246,0.2)",
                  }}
                />
              </ChartHeader>
              <ResponsiveContainer width="100%" height={420}>
                <AreaChart data={sortedByOrders} margin={{ top: 5, right: 20, left: -10, bottom: 60 }}>
                  <defs>
                    <linearGradient id="areaOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#818cf8" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="areaValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
                    axisLine={{ stroke: "rgba(148,163,184,0.1)" }}
                    tickLine={false}
                    angle={-40}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ color: "#94a3b8", fontSize: "0.8rem", paddingTop: "16px" }}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="orders"
                    stroke="#818cf8"
                    strokeWidth={2.5}
                    fill="url(#areaOrders)"
                    dot={{ r: 4, fill: "#818cf8", stroke: "#1e293b", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#818cf8", stroke: "#fff", strokeWidth: 2 }}
                    animationDuration={1500}
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="value"
                    stroke="#38bdf8"
                    strokeWidth={2.5}
                    fill="url(#areaValue)"
                    dot={{ r: 4, fill: "#38bdf8", stroke: "#1e293b", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#38bdf8", stroke: "#fff", strokeWidth: 2 }}
                    animationDuration={1500}
                    animationBegin={300}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>
      {/* ── Summary Footer ── */}
      <Grid container spacing={2.5}>
        <Grid minWidth={"100%"} item xs={12}>
          <GlassCard delay={0.7}>
            <CardContent sx={{ p: "1.5rem !important" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <IconWrapper bgcolor="rgba(99,102,241,0.15)">
                    <TrendingUpIcon sx={{ color: "#818cf8" }} />
                  </IconWrapper>
                  <Box>
                    <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: "1rem" }}>
                      Analytics Summary
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                      Data across all connected stores
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {[
                    { label: "Stores", value: ordersPerStoreData.length, color: "#818cf8" },
                    { label: "Orders", value: totalOrders, color: "#a78bfa" },
                    { label: "Suppliers", value: totalSuppliers, color: "#2dd4bf" },
                    { label: "Categories", value: totalCategories, color: "#60a5fa" },
                  ].map((stat, i) => (
                    <Chip
                      key={i}
                      label={`${stat.value} ${stat.label}`}
                      sx={{
                        background: `${stat.color}15`,
                        color: stat.color,
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        border: `1px solid ${stat.color}30`,
                        px: 1,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </GlassCard>
        </Grid>
      </Grid>
    </DashboardWrapper>
  );
};
export default Home;
